import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { JournalEntryModal } from '@/pages/accounting/journalEntry';
import { isoToDisplayDate } from '@/pages/accounting/statements/statementMeta';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { useVatSettlement } from './useVatSettlement';

type VatSettlementProps = {
    fromDate: string;
    toDate: string;
    netTetri: number;
    /** From the report. `null` = the ledger has no settlement account. */
    settlementAccountCode: string | null;
    /** Human period, e.g. "01/01/2026 – 24/07/2026", shown before commit. */
    periodText: string;
    /** Open the journal filtered to one account (the "review it" drill). */
    onViewLedger: (accountId: string) => void;
    /** Refetch the report after a settlement posts. */
    onSettled: () => void;
};

/**
 * Build the VAT settlement and hand the DRAFT to the Journal Entry form.
 * Render-only — the lifecycle lives in {@link useVatSettlement}.
 *
 * This is the one subsidiary handoff that is **open**, and the reason is worth
 * stating where it can be read: the figures behind it are derived from real
 * postings against real accounts, so the draft describes something true. Fixed
 * assets, payroll and dividends compute their runs from fabricated registers, so
 * theirs would put numbers into the ledger that describe nothing — their
 * `LedgerHandoffNotice` call sites stay exactly as they are.
 *
 * It still never posts on the user's behalf: it builds a balanced draft and
 * opens it in the shared form, whose validation, period gating, balance
 * indicator and Post button all apply unchanged.
 */
export const VatSettlement: React.FC<VatSettlementProps> = ({
    fromDate,
    toDate,
    netTetri,
    settlementAccountCode,
    periodText,
    onViewLedger,
    onSettled,
}) => {
    const { t } = useTranslation('pages');
    const settlement = useVatSettlement({
        fromDate,
        toDate,
        netTetri,
        settlementAccountCode,
        onSettled,
    });

    return (
        <section
            aria-label={t('vat.settlement.title')}
            className="flex flex-col gap-3 rounded-xl bg-surface-secondary p-4 print:hidden"
        >
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-col">
                    <span className="text-sm font-semibold text-foreground">
                        {t('vat.settlement.title')}
                    </span>
                    <span className="text-xs text-muted">
                        {t('vat.settlement.period', { period: periodText })}
                    </span>
                </div>
                <Button
                    isDisabled={settlement.block !== null}
                    isPending={settlement.isPreparing}
                    onPress={settlement.onPrepare}
                    variant="primary"
                    {...getNodeTestIdProp('vat-settlement-prepare')}
                >
                    <Icon aria-hidden icon="gravity-ui:file-check" />
                    {t('vat.settlement.prepare')}
                </Button>
            </div>

            {settlement.dueDate ? (
                <p
                    className="text-xs text-muted"
                    {...getNodeTestIdProp('vat-settlement-due')}
                >
                    {t('vat.settlement.dueDate', {
                        date: isoToDisplayDate(settlement.dueDate),
                    })}
                </p>
            ) : null}

            {settlement.block !== null ? (
                <p
                    className="flex items-center gap-2 text-xs text-muted"
                    {...getNodeTestIdProp('vat-settlement-reason')}
                >
                    <Icon aria-hidden icon="gravity-ui:circle-info" />
                    {settlement.block === 'ZERO_NET'
                        ? t('vat.settlement.zeroReason')
                        : t('vat.settlement.noAccountReason')}
                </p>
            ) : null}

            {settlement.existingCount > 0 && settlement.existingAccountId ? (
                <div
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-surface px-3 py-2"
                    {...getNodeTestIdProp('vat-settlement-existing')}
                >
                    <span className="flex items-center gap-2 text-xs text-foreground">
                        <Icon aria-hidden icon="gravity-ui:circle-info" />
                        {t('vat.settlement.existingNotice', {
                            count: settlement.existingCount,
                        })}
                    </span>
                    <Button
                        onPress={() =>
                            onViewLedger(settlement.existingAccountId as string)
                        }
                        size="sm"
                        variant="outline"
                        {...getNodeTestIdProp('vat-settlement-open-existing')}
                    >
                        {t('vat.settlement.openExisting')}
                    </Button>
                </div>
            ) : null}

            {settlement.prepareError ? (
                <p
                    className="text-xs text-danger"
                    {...getNodeTestIdProp('vat-settlement-error')}
                >
                    {settlement.prepareError}
                </p>
            ) : null}

            <JournalEntryModal
                entry={settlement.draft}
                isOpen={settlement.draft !== null}
                onClose={settlement.onClose}
                onSaved={settlement.onSaved}
            />
        </section>
    );
};
